import { useState, useEffect, useRef } from 'preact/hooks';
import { Cookie } from 'lucide-preact';
import { t } from '../lib/i18n';

// §7 — EU-base cookie/consent banner. PRIVACY-PRESERVING DEFAULT: non-essential
// cookies stay OFF until the visitor explicitly opts in. Not a dark pattern —
// "Essential only" is given equal visual weight (and is the safe default if the
// banner is dismissed). The choice is recorded server-side (POST /v1/consent).
const CONSENT_VERSION = '2026-06-01';
const LS_KEY = 'vs:cookie-consent';
const DEVICE_KEY = 'vs:creator:deviceId';

function getDeviceId(): string | undefined {
  try { return localStorage.getItem(DEVICE_KEY) ?? undefined; } catch { return undefined; }
}

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(LS_KEY)) setShow(true);
    } catch { /* storage blocked — don't nag */ }
  }, []);

  // While the banner is up, publish its height as --cookie-banner-clearance so
  // the bottom-anchored prompt dock (.chat-dock) floats ABOVE it instead of
  // being overlapped on a first visit (the banner has a max z-index). Cleared
  // the instant the visitor chooses, so the dock springs back down.
  useEffect(() => {
    const root = document.documentElement;
    if (!show) { root.style.setProperty('--cookie-banner-clearance', '0px'); return; }
    const measure = () => { const h = ref.current?.offsetHeight; if (h) root.style.setProperty('--cookie-banner-clearance', h + 'px'); };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      root.style.setProperty('--cookie-banner-clearance', '0px');
    };
  }, [show]);

  const choose = async (value: 'essential' | 'all') => {
    try { localStorage.setItem(LS_KEY, value); } catch { /* ignore */ }
    setShow(false);
    // Record the choice server-side (best-effort). Non-essential analytics only
    // initialize on 'all'; on 'essential' nothing extra loads.
    try {
      await fetch('/v1/consent', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ type: 'cookies', version: CONSENT_VERSION, value, deviceId: getDeviceId() })
      });
    } catch { /* best-effort */ }
    if (value === 'all') window.dispatchEvent(new Event('vs:cookies-accepted'));
  };

  if (!show) return null;

  return (
    <div
      ref={ref}
      data-testid="cookie-banner"
      role="dialog"
      aria-label={t('cookie.ariaPrefs')}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[2147483600] animate-vs-fade-in"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="pointer-events-auto mx-auto mb-3 flex max-w-[600px] flex-col gap-3 rounded-[var(--radius-lg)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] p-4 shadow-[0_18px_48px_-12px_rgba(0,0,0,0.32)] sm:flex-row sm:items-center sm:gap-4"
        style={{ marginLeft: 12, marginRight: 12 }}>
        {/* Icon + copy stay a single row at every width — on mobile the column
            layout used to orphan the icon alone on its own line above the text. */}
        <div className="flex flex-1 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-primary)]/12 text-[color:var(--color-primary)]">
            <Cookie size={17} />
          </span>
          <p className="text-[13px] leading-relaxed text-[color:var(--fg-muted)]">
            {t('cookie.msg')}{' '}
            <a href="/legal/privacy" className="font-medium text-[color:var(--color-primary)] hover:underline">{t('cookie.privacy')}</a>
          </p>
        </div>
        {/* Both choices read as real, equal-weight buttons (privacy-preserving,
            non-dark-pattern). The secondary gets a filled subtle surface + a
            strong border so it never reads as plain text next to the pink pill. */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => choose('essential')}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-[var(--radius-md)] border border-[color:var(--border-strong)] bg-[color:var(--bg-subtle)] px-4 text-[13px] font-semibold text-[color:var(--fg)] transition hover:bg-[color:var(--bg-overlay-active)] sm:flex-none"
          >
            {t('cookie.essential')}
          </button>
          <button
            type="button"
            onClick={() => choose('all')}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-4 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] active:scale-[0.98] sm:flex-none"
          >
            {t('cookie.all')}
          </button>
        </div>
      </div>
    </div>
  );
}
